import { Box, IconButton, Typography, Tabs, Tab, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { Delete, DragIndicator, Edit } from "@mui/icons-material";
import { Dispatch, SetStateAction, useEffect, useState, useMemo, useRef } from "react";
import { Vehicle } from "../../../state/types";
import { MediaFile } from "./UploadDialog";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { updateImageTag, deleteImage } from "./uploadRequests";
import "./upload-dialog.css";

interface ImageOrderListProps {
  vehicle: Vehicle;
  onChange: any;
  uploadedFiles: MediaFile[];
  setImageType: Dispatch<SetStateAction<string>>;
}

enum Direction {
  Up,
  Down,
}

// Item type for drag and drop
const ItemTypes = {
  IMAGE: 'image'
};

interface DraggableImageItemProps {
  image: any;
  index: number;
  currentType: string;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  handleTagChange: (imageIdx: number, newTag: string, imageType: string) => void;
  handleDelete: (imageIdx: number, imageType: string) => void;
  totalImages: number;
  isNew?: boolean;
}

/**
 * Single draggable image item component
 */
const DraggableImageItem = ({
  image,
  index,
  currentType,
  moveImage,
  handleTagChange,
  handleDelete,
  totalImages,
  isNew = false
}: DraggableImageItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tagValue, setTagValue] = useState(image.imageTag || '');

  // Set up drag
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.IMAGE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Set up drop
  const [, drop] = useDrop({
    accept: ItemTypes.IMAGE,
    hover: (item: { index: number }, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Time to actually perform the action
      moveImage(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  // Connect the drag and drop refs
  drag(drop(ref));

  // Handle tag editing
  const handleTagEdit = () => {
    setTagValue(image.imageTag || '');
    setIsDialogOpen(true);
  };

  const handleTagSave = () => {
    handleTagChange(index, tagValue, currentType);
    setIsDialogOpen(false);
  };

  const handleTagCancel = () => {
    setTagValue(image.imageTag || '');
    setIsDialogOpen(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTagSave();
    } else if (e.key === 'Escape') {
      handleTagCancel();
    }
  };

  // Handle delete functionality
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    handleDelete(index, currentType);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1,
        borderRadius: 1,
        bgcolor: isNew ? "rgba(46, 125, 50, 0.08)" : "background.paper",
        boxShadow: 1,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        mb: 2,
        position: 'relative'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
        <DragIndicator color="action" />
      </Box>

      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 2 }}>
        <img
          className="image-order-list-img"
          src={image.imageUrlCf}
          alt={image.imageTag || `Image ${index + 1}`}
          style={{ maxHeight: 100, objectFit: "contain" }}
        />

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                flexGrow: 1,
                minHeight: '20px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {image.imageTag || 'No tag'}
            </Typography>
            
            <IconButton
              size="small"
              onClick={handleTagEdit}
              aria-label="Edit tag"
            >
              <Edit fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              onClick={handleDeleteClick}
              aria-label="Delete image"
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>

          {isNew && (
            <Chip
              label="New"
              color="success"
              size="small"
            />
          )}
        </Box>
      </Box>

      {/* Edit Tag Dialog */}
      <Dialog open={isDialogOpen} onClose={handleTagCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Image Tag</DialogTitle>
        <DialogContent>
          <TextField
            value={tagValue}
            onChange={(e) => setTagValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Enter image tag..."
            fullWidth
            autoFocus
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTagCancel}>Cancel</Button>
          <Button onClick={handleTagSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm">
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Formats vehicle images into a structured object for easier manipulation
 */
const useVehicleImages = (vehicle: Vehicle) => {
  return useMemo(() => {
    const vehicleImages: Record<string, any[]> = {};
    const vehicleTypes: string[] = [];

    // Define all possible image types including potentially missing ones
    const imageTypes = [
      "imageExterior",
      "imageManual",
      "imagePano",
      "imageFeatured",
      "imageCrop",
      "imageUndercarriage",
      "imageBgrExterior"
    ];

    // Populate available image types
    imageTypes.forEach((type) => {
      // Ensure the property exists and is an array
      if (vehicle[type as keyof Vehicle] && Array.isArray(vehicle[type as keyof Vehicle])) {
        vehicleImages[type] = vehicle[type as keyof Vehicle] as any[];
        vehicleTypes.push(type);
      } else {
        vehicleTypes.push(type)
      }
    });

    return { vehicleImages, vehicleTypes };
  }, [vehicle]);
};

/**
 * Displays a list of ordered images with controls to change their sequence
 */
const ImageOrderList = ({ vehicle, onChange, uploadedFiles, setImageType }: ImageOrderListProps) => {
  const { vehicleImages, vehicleTypes } = useVehicleImages(vehicle);
  const [tabValue, setTabValue] = useState<string>(vehicleTypes[0] || "");

  // Track new image objects to show "New" indicator
  const [newImageObjects, setNewImageObjects] = useState<Record<string, Set<string>>>({});

  // Handle newly uploaded files
  useEffect(() => {
    if (uploadedFiles.length > 0) {
      const latestFile = uploadedFiles[uploadedFiles.length - 1];

      // Only add if we have a valid file and media group
      if (latestFile && latestFile.file && latestFile.mediaGroup) {
        addImage(latestFile.mediaGroup, latestFile.file);
      }
    }
  }, [uploadedFiles]);

  // Add a new image to the specified image type collection
  const addImage = (imageType: string, image: File) => {
    if (!vehicleImages[imageType]) {
      // Create the array if it doesn't exist yet
      vehicleImages[imageType] = [];
    }

    // Create a URL for the preview
    const objectUrl = URL.createObjectURL(image);

    // Create image object similar to existing format
    const newImage = {
      imageUrlCf: objectUrl,
      imageTag: `New Image ${vehicleImages[imageType].length + 1}`,
    };

    // Track this as a new image
    setNewImageObjects(prev => {
      const newMap = { ...prev };
      if (!newMap[imageType]) {
        newMap[imageType] = new Set();
      }
      newMap[imageType].add(objectUrl);
      return newMap;
    });

    const newImageList = [...vehicleImages[imageType], newImage];

    onChange({
      ...vehicle,
      [imageType]: newImageList,
    });

    console.log(newImageList)
  };

  // Reorder images within a collection
  const mutateOrder = (imageIdx: number, destination: number, imageType: string) => {
    if (
      !vehicleImages[imageType] ||
      destination < 0 ||
      destination >= vehicleImages[imageType].length
    ) {
      return;
    }

    const newImageList = [...vehicleImages[imageType]];
    const temp = newImageList[imageIdx];
    newImageList[imageIdx] = newImageList[destination];
    newImageList[destination] = temp;

    onChange({
      ...vehicle,
      [imageType]: newImageList,
    });
  };

  // Handle image tag changes
  const handleTagChange = async (imageIdx: number, newTag: string, imageType: string) => {
    if (!vehicleImages[imageType]) {
      return;
    }
  
    const result = await updateImageTag(vehicle, imageType, imageIdx, newTag);
    
    if (result.success) {
      // Update local state to reflect the change
      const newImageList = [...vehicleImages[imageType]];
      newImageList[imageIdx] = {
        ...newImageList[imageIdx],
        imageTag: newTag
      };
  
      onChange({
        ...vehicle,
        [imageType]: newImageList,
      });
    }
  };

  // Handle image deletion
  const handleDelete = async (imageIdx: number, imageType: string) => {
    if (!vehicleImages[imageType]) {
      return;
    }
  
    const result = await deleteImage(vehicle, imageType, imageIdx);
    
    if (result.success) {
      const imageToDelete = vehicleImages[imageType][imageIdx];
      
      // Clean up object URL if it's a new image
      if (imageToDelete?.imageUrlCf && imageToDelete.imageUrlCf.startsWith('blob:')) {
        URL.revokeObjectURL(imageToDelete.imageUrlCf);
      }
  
      // Remove from new images tracking
      if (newImageObjects[imageType]?.has(imageToDelete?.imageUrlCf)) {
        setNewImageObjects(prev => {
          const newMap = { ...prev };
          newMap[imageType].delete(imageToDelete.imageUrlCf);
          return newMap;
        });
      }
  
      // Remove from local state
      const newImageList = [...vehicleImages[imageType]];
      newImageList.splice(imageIdx, 1);
  
      onChange({
        ...vehicle,
        [imageType]: newImageList,
      });
    }
  };

  // Move an image using drag and drop
  const moveImage = (dragIndex: number, hoverIndex: number) => {
    mutateOrder(dragIndex, hoverIndex, tabValue);
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const selectedType = vehicleTypes[newValue];
    setTabValue(selectedType);
    setImageType(selectedType);
  };

  // No image types available
  if (vehicleTypes.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No images available</Typography>
      </Box>
    );
  }

  // Check if image is new based on tracked URLs
  const isNewImage = (imageType: string, image: any) => {
    return newImageObjects[imageType]?.has(image.imageUrlCf) || false;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="image-order-list-container">
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={vehicleTypes.indexOf(tabValue)}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .Mui-selected': {
                color: 'red !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'red',
              }
            }}
          >
            {vehicleTypes.map((type, index) => {
              const count = vehicleImages[type]?.length || 0;
              return (
                <Tab
                  key={type}
                  label={`${type.replace('image', '')} (${count})`}
                  value={index}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {vehicleImages[tabValue]?.length > 0 ? (
            vehicleImages[tabValue].map((image, idx) => (
              <DraggableImageItem
                key={`${tabValue}-${idx}-${image.imageUrlCf}`}
                image={image}
                index={idx}
                currentType={tabValue}
                moveImage={moveImage}
                handleTagChange={handleTagChange}
                totalImages={vehicleImages[tabValue].length}
                isNew={isNewImage(tabValue, image)}
                handleDelete={handleDelete}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No images in this category. Upload some images to get started.
            </Typography>
          )}
        </Box>
      </div>
    </DndProvider>
  );
};

export default ImageOrderList;